import React from 'react';
import { CheckCircleIcon, ShieldCheckIcon, BoltIcon, ArrowPathIcon } from '@heroicons/react/24/solid';

const features = [
  {
    icon: CheckCircleIcon,
    iconColor: 'text-green-500',
    iconBg: 'bg-green-50',
    title: 'Triple-verified emails',
    desc: 'Every email goes through syntax checks, MX record validation, and SMTP verification before it reaches you. No guessing, no bounces.',
    stat: '98%',
    statLabel: 'delivery rate',
  },
  {
    icon: ShieldCheckIcon,
    iconColor: 'text-indigo-500',
    iconBg: 'bg-indigo-50',
    title: 'GDPR & CCPA compliant',
    desc: 'All data is sourced ethically and processed in full compliance with GDPR, CCPA, and global data protection laws.',
    stat: '100%',
    statLabel: 'compliant',
  },
  {
    icon: ArrowPathIcon,
    iconColor: 'text-blue-500',
    iconBg: 'bg-blue-50',
    title: 'Weekly data refresh',
    desc: 'Our database is refreshed every week. Job changes, new hires, and company updates are captured so your lists never go stale.',
    stat: '52x',
    statLabel: 'refreshed yearly',
  },
  {
    icon: BoltIcon,
    iconColor: 'text-yellow-500',
    iconBg: 'bg-yellow-50',
    title: 'Real-time enrichment',
    desc: 'Enrich a single contact or upload a CSV of 100,000 rows — results come back in seconds, not hours, ready to export or sync.',
    stat: '<2s',
    statLabel: 'avg. response',
  },
];

export default function DataQuality() {
  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <p className="text-sm text-indigo-600 font-semibold uppercase tracking-wider mb-2">Our data</p>
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            Data you can actually trust
          </h2>
          <p className="text-gray-500 text-lg max-w-2xl mx-auto">
            We built Director Discovery around one principle: if you can't trust the data, nothing else matters. Here's how we make sure you can.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.title} className="bg-gray-50 rounded-2xl p-6 border border-gray-100 hover:border-indigo-200 hover:shadow-md transition-all group">
                <div className={`w-11 h-11 ${f.iconBg} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <Icon className={`w-6 h-6 ${f.iconColor}`} />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{f.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed mb-5">{f.desc}</p>
                <div className="pt-4 border-t border-gray-200">
                  <span className="text-2xl font-extrabold text-gray-900">{f.stat}</span>
                  <span className="text-sm text-gray-400 ml-2">{f.statLabel}</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom highlight bar */}
        <div className="mt-10 bg-gradient-to-r from-indigo-50 via-blue-50 to-indigo-50 rounded-2xl border border-indigo-100 p-6">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-indigo-600 rounded-xl flex items-center justify-center flex-shrink-0">
                <ShieldCheckIcon className="w-7 h-7 text-white" />
              </div>
              <div>
                <p className="font-bold text-gray-900">280M+ verified contacts in our database</p>
                <p className="text-sm text-gray-500">Continuously growing and refreshed. Covering 200+ countries and 30+ industries.</p>
              </div>
            </div>
            <div className="flex gap-8 flex-shrink-0">
              {[
                { val: '143M', label: 'Verified emails' },
                { val: '40M+', label: 'Mobile numbers' },
                { val: '15M+', label: 'Companies' },
              ].map((s) => (
                <div key={s.label} className="text-center">
                  <p className="text-2xl font-extrabold text-indigo-600">{s.val}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
